import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import EmailContact from "@/components/EmailContact";
import { COMPANY, DISCLAIMERS } from "@/lib/compliance";

export default function ContactPage() {
    const [params] = useSearchParams();
    const topic = params.get("topic");
    const isRecruitment = topic === "agent-recruitment";
    const recruitmentSubject =
        "Agent Recruitment \u2014 Application to Join Endurance Casualty";
    const recruitmentMailto = `mailto:${COMPANY.email}?subject=${encodeURIComponent(
        recruitmentSubject
    )}`;

    return (
        <PageShell
            testId="page-contact"
            kicker={isRecruitment ? "Agent Recruitment" : "Get in Touch"}
            title={
                isRecruitment
                    ? "Apply to Join Our Team"
                    : "Contact a Licensed Agent"
            }
            seoTitle={
                isRecruitment
                    ? "Agent Recruitment — Join Endurance Casualty"
                    : "Contact a Licensed Insurance Agent"
            }
            seoDescription={
                isRecruitment
                    ? "Endurance Casualty is recruiting licensed producers in NY, NJ, PA, CT, NC, FL, and TX. Send a brief introduction and we'll respond within one business day."
                    : "Speak with a licensed Endurance Casualty agent. Phone, email, mailing address, and a one-business-day response promise for personal, business, life, health, and cyber coverage."
            }
            lastUpdated="June 2026"
        >
            {isRecruitment && (
                <div
                    data-testid="contact-recruitment-banner"
                    className="not-prose mb-4 rounded-2xl border border-[rgba(212,175,55,0.3)] bg-gradient-to-br from-[#0a1426]/60 via-[#0b0e13] to-[#0b0e13] p-6 shadow-[0_18px_36px_-18px_rgba(0,0,0,0.6)]"
                >
                    <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Now Considering Licensed Producers
                    </p>
                    <h2 className="mt-3 font-serif text-2xl leading-tight text-brand-ivory md:text-3xl">
                        Interested in joining the {COMPANY.shortName} team?
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-brand-ivory/85">
                        We&apos;re reserving five advisor slots on our{" "}
                        <Link
                            to="/leadership"
                            className="text-brand-gold2 underline-offset-4 hover:underline"
                        >
                            leadership page
                        </Link>{" "}
                        for incoming licensed producers. To open a
                        conversation, send a brief email with your name,
                        state licenses and lines of authority, areas of
                        specialty, and a short bio. Please do not send
                        Social Security numbers, identity documents, or
                        other highly sensitive information by ordinary
                        email.
                    </p>
                    <a
                        data-testid="contact-recruitment-email"
                        href={recruitmentMailto}
                        className="mt-5 inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg shadow-[0_18px_36px_-18px_rgba(212,175,55,0.6)] transition-all hover:bg-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                    >
                        Email {COMPANY.email}
                    </a>
                    <p className="mt-3 text-[11px] text-brand-muted">
                        Subject line will be pre-filled with &ldquo;{recruitmentSubject}&rdquo;.
                    </p>
                </div>
            )}

            <p>
                Use any of the methods below to reach a licensed producer at{" "}
                {COMPANY.fullLegalName}. We typically respond within one
                business day.
            </p>

            <h2>Direct Contact</h2>
            <p>
                <strong>Phone</strong>:{" "}
                <a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phone}</a>
                <br />
                <strong>Email</strong>:{" "}
                <EmailContact variant="inline" testIdPrefix="email-contact" />
                <br />
                <strong>Mailing Address</strong>: {COMPANY.address}
            </p>
            <p>
                <em>
                    Clicking the email address opens a short, secure contact
                    form on this page so your message reaches a licensed
                    producer directly — no separate mail app needed.
                </em>
            </p>

            <h2>Online Inquiry</h2>
            <p>
                Prefer to send your details now? Use the inquiry form on our{" "}
                <Link to="/">home page</Link>. A licensed producer will follow
                up by phone or email.
            </p>

            <h2>What Not to Send by Email</h2>
            <p data-testid="contact-sensitive-warning">
                Please do not send Social Security numbers, payment
                information, bank information, medical records, claim
                documents, identity documents, passwords, or other highly
                sensitive information by unencrypted email or the general
                inquiry form. If we need sensitive information, we will
                provide instructions for an approved secure channel.
            </p>

            <h2>How We Use Your Information</h2>
            <p data-testid="contact-form-use-disclosure">
                We use the information you provide to respond to your
                inquiry, evaluate your request, communicate with licensed
                producers or insurance-market participants when appropriate,
                maintain communication and consent records, and comply with
                applicable law. Please review our{" "}
                <Link to="/privacy">Privacy Notice</Link> and{" "}
                <Link to="/terms">Terms of Service</Link> before submitting
                information.
            </p>

            <h2>Important Notices</h2>
            <p>{DISCLAIMERS.stateScope}</p>
            <p>{DISCLAIMERS.notInsurer}</p>
        </PageShell>
    );
}
